import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Sparkles, CheckCircle2, XCircle, AlertTriangle, PenLine } from "lucide-react";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { supportCode } from "@/lib/supportCode";
import ManualInvoiceForm from "./ManualInvoiceForm";

const MAX_FILES = 50;
const MAX_SIZE_MB = 10;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

export default function UploadInvoice() {
  const navigate = useNavigate();
  // Feb 2026 v17t: two entry modes — PDF upload (default, AI-powered) or
  // manual entry (no AI, 1 credit still, useful when no PDF is available).
  const [mode, setMode] = useState("upload");
  const [dragging, setDragging] = useState(false);
  const [paywallReason, setPaywallReason] = useState(null);
  const [queue, setQueue] = useState([]);
  const [batchDone, setBatchDone] = useState(false);
  // Duplicate confirmation dialog state. When set, the user has just tried
  // to upload a PDF that hashes identically to an existing one. They can
  // confirm ("carica comunque" → consumes 1 credit) or cancel.
  const [dupConfirm, setDupConfirm] = useState(null);
  const queueRef = useRef(queue);
  const processingRef = useRef(false);

  useEffect(() => { queueRef.current = queue; }, [queue]);

  const uploading = queue.some((q) => q.status === "uploading");

  const uploadOne = async (idx, { force = false } = {}) => {
    const item = queueRef.current[idx];
    if (!item || !item.file) return;
    setQueue((q) => {
      const clone = [...q];
      if (clone[idx]) clone[idx] = { ...clone[idx], status: "uploading" };
      return clone;
    });
    try {
      const fd = new FormData();
      fd.append("file", item.file);
      const url = force ? "/invoices/upload?force=true" : "/invoices/upload";
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQueue((q) => {
        const clone = [...q];
        if (clone[idx]) clone[idx] = { ...clone[idx], status: "done", id: data.id };
        return clone;
      });
      return { ok: true };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;

      // Duplicate soft-block (Feb 2026 v9): backend returns 409 with an
      // object detail describing the existing invoice. Pause the queue,
      // show a confirm dialog, then either force-retry or skip.
      if (status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_invoice") {
        setDupConfirm({
          idx,
          file_name: item.name,
          existing: {
            id: detail.existing_invoice_id,
            file_name: detail.existing_file_name,
            created_at: detail.existing_created_at,
            supplier: detail.existing_supplier,
            autofattura_number: detail.existing_autofattura_number,
            total_amount: detail.existing_total_amount,
          },
          message: detail.message,
        });
        setQueue((q) => {
          const clone = [...q];
          if (clone[idx]) clone[idx] = { ...clone[idx], status: "awaiting-confirm", message: "In attesa di conferma duplicato" };
          return clone;
        });
        return { ok: false, stop: true };
      }

      const errorText = typeof detail === "string" ? detail : (detail?.message || "Errore durante il caricamento");
      const isPaywall = status === 402;
      if (isPaywall) {
        setPaywallReason(errorText);
        setQueue((q) => q.map((it) =>
          it.status === "pending" || it.status === "uploading"
            ? { ...it, status: "error", message: it.status === "pending" ? "Crediti esauriti" : errorText }
            : it
        ));
        return { ok: false, stop: true };
      }
      setQueue((q) => {
        const clone = [...q];
        if (clone[idx]) clone[idx] = { ...clone[idx], status: "error", message: errorText };
        return clone;
      });
      return { ok: false };
    }
  };

  const runQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (true) {
        const idx = queueRef.current.findIndex((it) => it.status === "pending");
        if (idx === -1) break;
        const res = await uploadOne(idx);
        if (res && res.stop) break;
      }
      setBatchDone(true);
    } finally {
      processingRef.current = false;
    }
  }, []);

  const confirmDuplicate = async () => {
    if (!dupConfirm) return;
    const idx = dupConfirm.idx;
    setDupConfirm(null);
    // Re-open the item as "pending" so uploadOne can process it, then
    // force-retry immediately.
    setQueue((q) => {
      const clone = [...q];
      if (clone[idx]) clone[idx] = { ...clone[idx], status: "pending", message: null };
      return clone;
    });
    await uploadOne(idx, { force: true });
    // Continue with the rest of the queue.
    runQueue();
  };

  const skipDuplicate = () => {
    if (!dupConfirm) return;
    const idx = dupConfirm.idx;
    setQueue((q) => {
      const clone = [...q];
      if (clone[idx]) clone[idx] = { ...clone[idx], status: "error", message: "Duplicato — non caricato" };
      return clone;
    });
    setDupConfirm(null);
    runQueue();
  };

  const enqueue = useCallback((files) => {
    if (!files || files.length === 0) return;
    setBatchDone(false);
    const arr = Array.from(files);
    if (arr.length > MAX_FILES) {
      toast.error(`Massimo ${MAX_FILES} file per volta. Ne hai selezionati ${arr.length}`);
      arr.length = MAX_FILES;
    }
    const items = arr.map((f) => {
      const isPdf = f.name.toLowerCase().endsWith(".pdf");
      const tooBig = f.size > MAX_SIZE;
      const status = !isPdf ? "error" : tooBig ? "error" : "pending";
      const message = !isPdf ? "Non è un PDF"
        : tooBig ? `Troppo grande (${(f.size / 1024 / 1024).toFixed(1)} MB > ${MAX_SIZE_MB} MB)`
        : null;
      const key = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${f.name}-${f.size}-${Date.now()}`;
      return { key, file: f, name: f.name, size: f.size, status, message };
    });
    setQueue((prev) => [...prev, ...items].slice(0, MAX_FILES));
    setTimeout(() => { runQueue(); }, 50);
  }, [runQueue]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    enqueue(e.dataTransfer.files);
  };

  const doneCount = queue.filter((q) => q.status === "done").length;
  const errorCount = queue.filter((q) => q.status === "error").length;
  const totalDone = doneCount + errorCount;

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="upload-page">
      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">
          {mode === "upload" ? "Carica le tue fatture estere" : "Compila un'autofattura"}
        </h1>
        <p className="text-zinc-600 mt-1">
          {mode === "upload"
            ? `Fino a ${MAX_FILES} PDF per volta, massimo ${MAX_SIZE_MB} MB ciascuno. L'AI estrae automaticamente tutti i dati fiscali`
            : "Compila i campi dell'autofattura senza caricare un PDF — utile quando hai solo un'email o dati sciolti"}
        </p>
      </div>

      {/* Mode switcher — segmented control */}
      <div className="inline-flex items-center gap-1 rounded-full bg-[#F5F2EA] border border-[#E8E4D9] p-1 mb-6" data-testid="upload-mode-switcher">
        <button
          type="button"
          onClick={() => setMode("upload")}
          data-testid="upload-mode-pdf"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            mode === "upload"
              ? "bg-zinc-900 text-white shadow-sm"
              : "text-[#4A4A4A] hover:text-[#0A0A0A]"
          }`}
        >
          <Upload className="w-4 h-4" />
          Carica PDF
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          data-testid="upload-mode-manual"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            mode === "manual"
              ? "bg-zinc-900 text-white shadow-sm"
              : "text-[#4A4A4A] hover:text-[#0A0A0A]"
          }`}
        >
          <PenLine className="w-4 h-4" />
          Compila manualmente
        </button>
      </div>

      {mode === "manual" ? (
        <ManualInvoiceForm onPaywall={(reason) => setPaywallReason(reason)} />
      ) : (
      <>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`dropzone rounded-2xl bg-white p-14 text-center ${dragging ? "dragging" : ""}`}
        data-testid="upload-dropzone"
      >
        <div className="w-16 h-16 rounded-full bg-[#F5F2EA] grid place-items-center mx-auto mb-5">
          {uploading ? <Loader2 className="w-7 h-7 text-zinc-700 animate-spin" /> : <Upload className="w-7 h-7 text-zinc-700" />}
        </div>
        <h2 className="font-display font-semibold text-xl mb-2">
          {uploading ? `Caricamento in corso (${totalDone + 1}/${queue.length})` : "Trascina qui i tuoi PDF"}
        </h2>
        <p className="text-sm text-zinc-600 mb-5">o seleziona uno o più file dal tuo computer</p>
        <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 cursor-pointer transition-colors" data-testid="upload-select-btn">
          <FileText className="w-4 h-4" />
          Seleziona PDF
          <input
            type="file"
            multiple
            accept="application/pdf,.pdf"
            className="hidden"
            disabled={uploading}
            onChange={(e) => { enqueue(e.target.files); e.target.value = ""; }}
            data-testid="upload-input"
          />
        </label>
        <p className="mt-4 text-xs text-zinc-500">Max {MAX_FILES} file · {MAX_SIZE_MB} MB per file · PDF di Google, Meta, Amazon, Zoom</p>
      </div>

      {queue.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#E8E4D9] bg-white p-4" data-testid="upload-queue">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm">
              <strong className="text-emerald-700">{doneCount}</strong> caricati ·
              <strong className="text-red-700 ml-1">{errorCount}</strong> errori ·
              <strong className="text-[#0A0A0A] ml-1">{queue.length}</strong> totali
            </div>
            {batchDone && doneCount > 0 && (
              <button
                onClick={() => navigate("/app/invoices")}
                data-testid="upload-goto-list"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#1D4ED8]"
              >
                Vai a Fatture →
              </button>
            )}
          </div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {queue.map((it, i) => (
              <div key={it.key ?? i} className="flex items-center gap-2 text-xs py-1 border-b border-[#E8E4D9]/50 last:border-0" data-testid={`upload-queue-item-${i}`}>
                {it.status === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                {it.status === "uploading" && <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin shrink-0" />}
                {it.status === "error" && <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                {it.status === "awaiting-confirm" && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                {it.status === "pending" && <div className="w-3.5 h-3.5 rounded-full border border-zinc-300 shrink-0" />}
                <span className="font-mono truncate flex-1">{it.name}</span>
                <span className="text-zinc-500 tabular-nums">{(it.size / 1024 / 1024).toFixed(2)} MB</span>
                {it.message && <span className="text-red-600 italic max-w-[200px] truncate">{it.message}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duplicate confirmation dialog */}
      {dupConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-6" data-testid="duplicate-dialog">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-[#E8E4D9] flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <div className="font-display font-bold text-lg text-[#0A0A0A]">Fattura duplicata</div>
                <div className="text-sm text-[#4A4A4A] mt-1">
                  Il file <span className="font-mono text-xs bg-[#F5F2EA] px-1 rounded">{dupConfirm.file_name}</span>
                  {" "}è già presente nel sistema. Se procedi, ti verrà consumato <strong>1 credito</strong> come per un caricamento normale.
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-[#FAF9F6] text-xs space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-1">Fattura esistente</div>
              <div className="flex gap-2">
                <span className="text-[#737373] w-24 shrink-0">Codice:</span>
                <span className="font-mono font-bold text-[#1D4ED8]">{supportCode(dupConfirm.existing.id)}</span>
              </div>
              {dupConfirm.existing.autofattura_number && (
                <div className="flex gap-2">
                  <span className="text-[#737373] w-24 shrink-0">Autofattura:</span>
                  <span className="font-mono text-[#0A0A0A]">{dupConfirm.existing.autofattura_number}</span>
                </div>
              )}
              {dupConfirm.existing.supplier && (
                <div className="flex gap-2">
                  <span className="text-[#737373] w-24 shrink-0">Fornitore:</span>
                  <span className="text-[#0A0A0A]">{dupConfirm.existing.supplier}</span>
                </div>
              )}
              {dupConfirm.existing.total_amount != null && (
                <div className="flex gap-2">
                  <span className="text-[#737373] w-24 shrink-0">Importo:</span>
                  <span className="font-mono tabular-nums text-[#0A0A0A]">
                    € {Number(dupConfirm.existing.total_amount).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <span className="text-[#737373] w-24 shrink-0">Caricata il:</span>
                <span className="text-[#0A0A0A]">{(dupConfirm.existing.created_at || "").slice(0, 10)}</span>
              </div>
            </div>
            <div className="p-4 flex justify-end gap-2">
              <button
                onClick={skipDuplicate}
                data-testid="duplicate-skip-btn"
                className="px-4 py-2 rounded-md border border-[#E8E4D9] text-[#0A0A0A] text-sm font-medium hover:bg-[#F5F2EA] transition-colors"
              >
                Annulla, è un errore
              </button>
              <button
                onClick={confirmDuplicate}
                data-testid="duplicate-force-btn"
                className="px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors"
              >
                Carica comunque (1 credito)
              </button>
            </div>
          </div>
        </div>
      )}

      <BuyCreditsModal open={!!paywallReason} onClose={() => setPaywallReason(null)} reason={paywallReason} />

      <div className="mt-8 rounded-xl border border-[#E8E4D9] bg-white p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-md bg-blue-50 grid place-items-center">
            <Sparkles className="w-4 h-4 text-[#1D4ED8]" />
          </div>
          <h3 className="font-display font-semibold">Come funziona</h3>
        </div>
        <ol className="space-y-2 text-sm text-zinc-700">
          <li><span className="font-mono text-[#1D4ED8] mr-2">01</span> Carichi uno o più PDF ricevuti dai fornitori esteri (max {MAX_FILES} per volta)</li>
          <li><span className="font-mono text-[#1D4ED8] mr-2">02</span> L&apos;AI estrae fornitore, VAT ID, importo, valuta, tipo servizio per ogni PDF</li>
          <li><span className="font-mono text-[#1D4ED8] mr-2">03</span> Verifichi i campi in review e clicchi «Genera XML»</li>
          <li><span className="font-mono text-[#1D4ED8] mr-2">04</span> Scarichi i file XML pronti per SDI o per il commercialista</li>
        </ol>
      </div>
      </>
      )}
    </div>
  );
}
